import type { BuildHubTypeEnum } from 'generated/graphql';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';
import type { BudgetRange } from '@modules/build-hub-creator/api/types';

/**
 * Stage input for BuildHub creation/update
 */
export interface StageInput {
    urlSegment: string;
    title: string;
    isActive?: boolean;
    isCompleted?: boolean;
    startDate?: string | null;
    completionDate?: string | null;
    progress?: number;
    isDisabled?: boolean;
}

/**
 * Milestone input for BuildHub creation/update
 */
interface MilestoneInput {
    urlSegment: string;
    title: string;
    isCompleted?: boolean;
    date?: string | null;
    isDisabled?: boolean;
}

/**
 * Form data structure for building FormData updates
 */
interface BuildHubUpdateData {
    title?: string;
    description?: string;
    projectType?: BuildHubTypeEnum;
    scope?: string; // Display label (e.g., "New Home Build", "Pool or Spa Installation")
    stages?: StageInput[]; // Array of stages to create for this BuildHub
    milestones?: MilestoneInput[]; // Array of milestones to create for this BuildHub
    activeStageURLSegment?: string; // URL segment of the active stage
    ownerTeamMemberID?: string; // TeamMemberID for owner if representing a professional
    ownerCustomCompanyName?: string; // Custom company name if not linked to existing professional
    location?:
        | {
              selectedLocation: DetailedPlace | null;
              keyword: string;
          }
        | {
              formattedAddress: string;
              place: DetailedPlace;
          };
    timeline?: {
        startDate?: string;
        endDate?: string;
        isNotSure?: boolean;
    };
    budget?: BudgetRange;
    thumbnailFile?: File;
}

/**
 * Constructs FormData object for BuildHub update operations
 *
 * This utility encapsulates the complex logic of mapping form data to the
 * GraphQL mutation format, handling different data structures and optional fields.
 *
 * @param buildHubId - The ID of the BuildHub to update
 * @param data - The form data to be submitted
 * @returns FormData object ready for submission
 */
export function buildFormDataForUpdate(
    buildHubId: string,
    data: BuildHubUpdateData,
): FormData {
    const formDataObj = new FormData();

    // Required fields
    formDataObj.append('intent', 'update');
    formDataObj.append('id', buildHubId);

    // Title (optional for partial updates)
    if (data.title) {
        formDataObj.append('title', data.title);
    }

    // Description (optional)
    if (data.description) {
        formDataObj.append('description', data.description);
    }

    // Project type (optional for partial updates)
    if (data.projectType) {
        formDataObj.append('projectType', data.projectType);
    }

    // Project scope (optional)
    if (data.scope) {
        formDataObj.append('projectScope', data.scope);
    }

    // Stages array (optional) - send as JSON string
    if (data.stages && data.stages.length > 0) {
        const stagesJson = JSON.stringify(data.stages);
        console.log('📦 Adding stages to FormData:', stagesJson);
        formDataObj.append('stages', stagesJson);
    } else {
        console.log('⚠️ No stages to add to FormData:', data.stages);
    }

    // Active stage URL segment (optional)
    if (data.activeStageURLSegment) {
        formDataObj.append('activeStageURLSegment', data.activeStageURLSegment);
    }

    // Owner team member ID (optional)
    if (data.ownerTeamMemberID) {
        formDataObj.append('ownerTeamMemberID', data.ownerTeamMemberID);
    }

    // Owner custom company name (optional)
    if (data.ownerCustomCompanyName) {
        formDataObj.append(
            'ownerCustomCompanyName',
            data.ownerCustomCompanyName.trim(),
        );
    }

    // Location mapping - serialize DetailedPlace as PlaceDetailType
    if (data.location) {
        let selectedPlace: DetailedPlace | null = null;

        if ('formattedAddress' in data.location) {
            // LocationData format: { place: DetailedPlace, formattedAddress: string }
            selectedPlace = data.location.place;
        } else if ('selectedLocation' in data.location) {
            // ActualLocationData format: { selectedLocation: DetailedPlace, keyword: string }
            selectedPlace = data.location.selectedLocation;
        }

        // Only send locationData if we have a valid fullAddress (required by GraphQL schema)
        if (
            selectedPlace &&
            selectedPlace.fullAddress &&
            selectedPlace.fullAddress.trim()
        ) {
            // Map DetailedPlace (camelCase) to PlaceDetailType (PascalCase)
            const locationData = {
                Latitude: selectedPlace.latitude,
                Longitude: selectedPlace.longitude,
                FullAddress: selectedPlace.fullAddress,
                Address1: selectedPlace.address1,
                Address2: selectedPlace.address2,
                Suburb: selectedPlace.suburb,
                City: selectedPlace.city,
                Region: selectedPlace.region,
                State: selectedPlace.state,
                Country: selectedPlace.country,
                PostCode: selectedPlace.postCode,
                PlaceName: selectedPlace.placeName,
                PlaceType: selectedPlace.placeType,
                AddressID: selectedPlace.addressID,
                Depth: selectedPlace.depth,
                ParentID: selectedPlace.parentID,
                ServiceableAreaID: selectedPlace.serviceableAreaID,
                District: selectedPlace.district,
                Division: selectedPlace.division,
                Slug: selectedPlace.slug,
            };

            formDataObj.append('locationData', JSON.stringify(locationData));
        }
    }

    // Timeline mapping
    if (data.timeline) {
        if (data.timeline.startDate) {
            const start = data.timeline.startDate.slice(0, 10);
            formDataObj.append('startDate', start);
        }

        if (data.timeline.endDate && !data.timeline.isNotSure) {
            const end = data.timeline.endDate.slice(0, 10);
            formDataObj.append('endDate', end);
        }
    }

    // Budget mapping
    if (data.budget) {
        if (typeof data.budget.min === 'number') {
            formDataObj.append('budget_min', data.budget.min.toString());
        }
        if (typeof data.budget.max === 'number') {
            formDataObj.append('budget_max', data.budget.max.toString());
        }
    }

    // Thumbnail file (optional)
    if (data.thumbnailFile) {
        formDataObj.append('thumbnailFile', data.thumbnailFile);
    }

    return formDataObj;
}
